import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ChevronRight } from "lucide-react";
import heroImage from "../../assets/images/contact/home4.jpg";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

// Firm Contact Component
const FirmContact = ({ name, tagline, url, isLast }) => {
  return (
    <div
      className={`flex justify-between items-center py-6 md:py-8 border-t border-gray-200 ${
        isLast ? "border-b" : ""
      }`}
    >
      <div>
        <h3 className="text-xl md:text-2xl font-medium">{name}</h3>
        <p className="text-[14px] md:text-[15px] leading-relaxed text-gray-800 mt-2 font-serif">
          {tagline}
        </p>
      </div>
      <div className="group">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm group-hover:opacity-70"
        >
          <span className="relative text-base md:text-lg font-medium hidden md:inline">
            Contact
            {/* <span className="absolute -bottom-1 left-0 w-full h-[2px] bg-black"></span> */}
          </span>
          <ChevronRight className="w-5 h-5 ml-2 md:ml-0" />
        </a>
      </div>
    </div>
  );
};

const ContactPage = () => {
  // Refs for GSAP animations
  const heroTitleRef = useRef(null);
  const heroTextRef = useRef(null);
  const heroImageRef = useRef(null);
  const firmRefs = useRef([]);

  // Array of architecture firms with taglines and urls
  const firms = [
    {
      name: "Gensler",
      tagline: "Designing a better world through innovative architecture.",
      url: "https://www.gensler.com/contact",
    },
    {
      name: "Arcadis",
      tagline: "Improving quality of life with sustainable solutions.",
      url: "https://www.arcadis.com/en/contact",
    },
    {
      name: "HDR (Henningson, Durham & Richardson)",
      tagline: "Transforming lives through exceptional design and engineering.",
      url: "https://www.hdrinc.com/contact-us",
    },
    {
      name: "Nikken Sekkei",
      tagline: "Creating value through integrated design and technology.",
      url: "https://www.nikken.co.jp/en/contacts/index.html",
    },
    {
      name: "AECOM",
      tagline: "Delivering a better world with global expertise.",
      url: "https://aecom.com/contact-us/",
    },
    {
      name: "Perkins + Will",
      tagline: "Human-centered design for a sustainable future.",
      url: "https://perkinswill.com/contact/",
    },
    {
      name: "Skidmore, Owings & Merrill (SOM)",
      tagline: "Pioneering architecture and urban planning worldwide.",
      url: "https://www.som.com/about/studios/#contact-us",
    },
    {
      name: "Foster + Partners",
      tagline: "Innovative architecture for a changing world.",
      url: "https://www.fosterandpartners.com/contact/",
    },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);

    // Hero section animations
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

    tl.fromTo(
      heroTitleRef.current,
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 1 }
    )
      .fromTo(
        heroTextRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 1 },
        "-=0.5"
      )
      .fromTo(
        heroImageRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.2 },
        "-=0.5"
      );

    // Firms section animations
    gsap.fromTo(
      firmRefs.current,
      {
        y: 30,
        opacity: 0,
      },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: firmRefs.current[0],
          start: "top 80%",
        },
      }
    );
  }, []);

  return (
    <div className="">
      {/* Hero Section */}
      <section className="my-8 md:my-16 max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 items-start md:items-end mb-8 md:mb-16">
          <h1
            ref={heroTitleRef}
            className="text-3xl md:text-[40px] leading-[1.2] font-medium"
          >
            Reach Them Here
          </h1>
          <p
            ref={heroTextRef}
            className="text-[14px] md:text-[15px] leading-relaxed text-gray-800 font-medium mt-2 font-serif"
          >
            Connect directly with leading architecture firms. Find contact
            details for global innovators in design and engineering below.
          </p>
        </div>
      </section>

      <section className="">
        <div className="w-full">
          <img
            ref={heroImageRef}
            src={heroImage}
            alt="Modern architectural building with glass facade and wooden details"
            className="w-full h-[300px] md:h-[600px] object-cover"
          />
        </div>
      </section>

      {/* Contact Links Section */}
      <section className="my-16 md:my-32 max-w-[1400px] mx-auto px-4 md:px-8">
        <div>
          {firms.map((firm, index) => (
            <div key={firm.name} ref={(el) => (firmRefs.current[index] = el)}>
              <FirmContact
                name={firm.name}
                tagline={firm.tagline}
                url={firm.url}
                isLast={index === firms.length - 1}
              />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
